/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export function DataArray(data, shape) {
    this.data = data;
    this.shape = shape;
}

function RLEObject(size, counts) {
    this.size = size;
    this.counts = counts;
}

function RLE(h, w, m, cnts) {
    this.h = h;
    this.w = w;
    this.m = m;
    this.cnts = cnts;
}

function BB() {
    return [];
}

function rleInit(R, h, w, m, cnts) {
    R.h = h;
    R.w = w;
    R.m = m;
    R.cnts = m === 0 ? [0] : cnts;
}

function rlesInit(n) {
    var R = [];
    for (var i = 0; i < n; i++) {
        R[i] = new RLE(0, 0, 0, [0]);
        rleInit(R[i], 0, 0, 0, [0]);
    }
    return R;
}

function RLEs(n) {
    this._R = rlesInit(n);
    this._n = n;
}

function Masks(h, w, n) {
    this._mask = new Uint8Array(h * w * n);
    this._h = h;
    this._w = w;
    this._n = n;
}

Masks.prototype.toDataArray = function () {
    return new DataArray(this._mask, [this._h, this._w, this._n]);
};

export function encode(mask) {
    var h = mask.shape[0];
    var w = mask.shape[1];
    var n = mask.shape[2];
    var Rs = new RLEs(n);
    rleEncode(Rs._R, mask.data, h, w, n);
    return _toString(Rs);
}

export function decode(rleObjs) {
    var Rs = _frString(rleObjs);
    var h = Rs._R[0].h;
    var w = Rs._R[0].w;
    var n = Rs._n;
    var masks = new Masks(h, w, n);
    rleDecode(Rs._R, masks._mask, n);
    return masks.toDataArray();
}

export function toBbox(rleObjs) {
    var Rs = _frString(rleObjs);
    var n = Rs._n;
    var bb = new BB();
    rleToBbox(Rs._R, bb, n);
    return bb;
}

/**
 * 将二值掩码数据转换为带颜色的ImageData
 * @param {Uint8Array} maskData - 包含0和1的掩码数据
 * @param {number} width - 图像宽度
 * @param {number} height - 图像高度
 * @param {Object} color - 为1 的颜色配置 {r,g,b,a}
 * @param {Object} color2 - 为0 的颜色配置 {r,g,b,a}
 * @returns {ImageData} 转换后的ImageData对象
 */
export function convertMaskToColorImageData(
    maskData,
    width,
    height,
    color = { r: 255, g: 255, b: 255, a: 255 },
    color2 = { r: 0, g: 0, b: 0, a: 255 },
) {
    const rgba = new Uint8ClampedArray(width * height * 4);

    for (let i = 0; i < maskData.length; i++) {
        const pos = i * 4;

        if (maskData[i] === 1) {
            // 使用指定颜色
            rgba[pos] = color.r; // R
            rgba[pos + 1] = color.g; // G
            rgba[pos + 2] = color.b; // B
            rgba[pos + 3] = color.a; // A
        } else {
            // 透明背景
            rgba[pos] = color2.r;
            rgba[pos + 1] = color2.g;
            rgba[pos + 2] = color2.b;
            rgba[pos + 3] = color2.a;
        }
    }

    const flipped = _flipRGBAVertically(rgba, height, width);
    const rotated = _rotateRGBA90CounterClockwise(flipped, height, width);

    return new ImageData(rotated, width, height);
}

// 优化版本
/**
 * 将RGBA数据逆时针旋转90度（优化版本）
 * @param {Uint8ClampedArray} rgba - RGBA数据
 * @param {number} width - 原始图像宽度
 * @param {number} height - 原始图像高度
 * @returns {Object} 包含旋转后的数据和新的尺寸
 */
function _rotateRGBA90CounterClockwise(rgba, width, height) {
    const rotated = new Uint8ClampedArray(width * height * 4);
    const pixel = new Uint8Array(4);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const srcIndex = (y * width + x) * 4;
            const newX = height - 1 - y;
            const newY = x;
            const dstIndex = (newY * height + newX) * 4;

            // 一次性读取4个通道
            pixel[0] = rgba[srcIndex];
            pixel[1] = rgba[srcIndex + 1];
            pixel[2] = rgba[srcIndex + 2];
            pixel[3] = rgba[srcIndex + 3];

            // 一次性写入4个通道
            rotated.set(pixel, dstIndex);
        }
    }
    return rotated;
}

/**
 * 将RGBA数据上下翻转
 * @param {Uint8ClampedArray} rgba - RGBA数据
 * @param {number} width - 图像宽度
 * @param {number} height - 图像高度
 * @returns {Uint8ClampedArray} 翻转后的RGBA数据
 */
function _flipRGBAVertically(rgba, width, height) {
    // 创建新的数组来存储翻转后的数据
    const flipped = new Uint8ClampedArray(width * height * 4);

    // 每行的字节数（宽度 * 4个通道）
    const stride = width * 4;

    for (let y = 0; y < height; y++) {
        // 源数据的行起始位置（从上到下）
        const srcRow = y * stride;
        // 目标数据的行起始位置（从下到上）
        const dstRow = (height - 1 - y) * stride;

        // 复制整行数据
        for (let x = 0; x < stride; x++) {
            flipped[dstRow + x] = rgba[srcRow + x];
        }
    }

    return flipped;
}

function rleEncode(R, M, h, w, n) {
    var a = w * h;
    for (var i = 0; i < n; i++) {
        var from = a * i;
        var to = a * (i + 1);
        var T = M.slice(from, to);
        var cnts = [];
        var k = 0;
        var p = 0;
        var c = 0;
        for (var j = 0; j < a; j++) {
            if (T[j] !== p) {
                cnts[k++] = c;
                c = 0;
                p = T[j];
            }
            c++;
        }
        cnts[k++] = c;
        rleInit(R[i], h, w, k, cnts);
    }
}

function rleDecode(R, M, n) {
    var p = 0;
    for (var i = 0; i < n; i++) {
        var v = false;
        for (var j = 0; j < R[i].m; j++) {
            for (var k = 0; k < R[i].cnts[j]; k++) {
                M[p++] = v ? 1 : 0;
            }
            v = !v;
        }
    }
}

function rleToString(R) {
    var s = [];
    var p = 0;
    for (var i = 0; i < R.m; i++) {
        var x = R.cnts[i];
        if (i > 2) {
            x -= R.cnts[i - 2];
        }
        var more = true;
        while (more) {
            var c = x & 0x1f;
            x >>= 5;
            more = c & 0x10 ? x !== -1 : x !== 0;
            if (more) {
                c |= 0x20;
            }
            c += 48;
            s[p++] = String.fromCharCode(c);
        }
    }
    return s.join('');
}

function _toString(Rs) {
    var objs = [];
    for (var i = 0; i < Rs._n; i++) {
        var c_string = rleToString(Rs._R[i]);
        objs.push(new RLEObject([Rs._R[i].h, Rs._R[i].w], c_string));
    }
    return objs;
}

function _frString(rleObjs) {
    var Rs = new RLEs(rleObjs.length);
    for (var i = 0; i < rleObjs.length; i++) {
        var obj = rleObjs[i];
        rleFrString(Rs._R[i], obj.counts, obj.size[0], obj.size[1]);
    }
    return Rs;
}

function rleToBbox(R, bb, n) {
    for (var i = 0; i < n; i++) {
        var h = R[i].h;
        var w = R[i].w;
        var m = Math.floor(R[i].m / 2) * 2;
        var xs = w;
        var ys = h;
        var xe = 0;
        var ye = 0;
        var cc = 0;
        var xp = 0;
        if (m === 0) {
            bb[4 * i] = bb[4 * i + 1] = bb[4 * i + 2] = bb[4 * i + 3] = 0;
            continue;
        }
        for (var j = 0; j < m; j++) {
            cc += R[i].cnts[j];
            var t = cc - (j % 2);
            var y = t % h;
            var x = Math.floor((t - y) / h);
            if (j % 2 === 0) {
                xp = x;
            } else if (xp < x) {
                ys = 0;
                ye = h - 1;
            }
            xs = Math.min(xs, x);
            xe = Math.max(xe, x);
            ys = Math.min(ys, y);
            ye = Math.max(ye, y);
        }
        bb[4 * i] = xs;
        bb[4 * i + 2] = xe - xs + 1;
        bb[4 * i + 1] = ys;
        bb[4 * i + 3] = ye - ys + 1;
    }
}

function rleFrString(R, s, h, w) {
    var cnts = [];
    var m = 0;
    var p = 0;
    while (s[p]) {
        var x = 0;
        var k = 0;
        var more = 1;
        while (more) {
            var c = s.charCodeAt(p) - 48;
            x |= (c & 0x1f) << (5 * k);
            more = c & 0x20;
            p++;
            k++;
            if (!more && c & 0x10) {
                x |= -1 << (5 * k);
            }
        }
        if (m > 2) {
            x += cnts[m - 2];
        }
        cnts[m++] = x;
    }
    rleInit(R, h, w, m, cnts);
}

export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
              a: result[4] != null ? parseInt(result[4], 16) : 128,
          }
        : { r: 255, g: 0, b: 0, a: 128 };
}
